import type { NavFlags } from "$lib/launchdarkly"
import { cn } from "@tastyworks/ui-library"
import { cva } from "class-variance-authority"
import type { ComponentType } from "svelte"
import { get } from "svelte/store"

export type NavGroup = {
  href: string
  id: string
  items: Array<NavItem>
  hidden?: boolean
}

export type NavItem = {
  href: string
  id: string
  hidden?: boolean
  component?: ComponentType
  items?: Array<NavItem>
}

/**
 * enabled - clickable. Already completed groups and sections
 * disabled - not clickable. Groups and sections that have not been reached yet
 * active - currently on this group or section
 * in-progress - furthest reached group or section
 * review - completed all forms.
 * complete - completed all forms. nothing is clickable
 */

export type NavItemState = "enabled" | "disabled" | "in-progress" | "review"

export class StepNode {
  value: NavItem
  next: StepNode | null = null
  prev: StepNode | null = null
  index: number
  groupIndex: number

  constructor(value: NavItem, index: number, groupIndex: number) {
    this.value = value
    this.index = index
    this.groupIndex = groupIndex
  }
}
export class LinkedList {
  head: StepNode | null = null
  tail: StepNode | null = null

  push(value: NavItem, index: number, groupIndex: number): void {
    const newNode = new StepNode(value, index, groupIndex)

    if (!this.head) {
      this.head = newNode
      this.tail = newNode
    } else {
      newNode.prev = this.tail
      this.tail!.next = newNode
      this.tail = newNode
    }
  }

  *forward(): Generator<StepNode, void, unknown> {
    let current: StepNode | null = this.head
    while (current) {
      yield current
      current = current.next
    }
  }

  *backward(): Generator<StepNode, void, unknown> {
    let current = this.tail
    while (current) {
      yield current
      current = current.prev
    }
  }
}

export function buildNavSteps(navGroups: Array<NavGroup>) {
  const list = new LinkedList()

  let index = 0
  let groupIndex = 0

  function buildLinkedList(curStep: NavItem, path: string, id: string) {
    if (curStep.hidden) return

    if (!curStep.items) {
      const newStep: NavItem = {
        ...curStep,
        id: id ? [id, curStep.id].join(".") : curStep.id,
        href: path + curStep.href,
      }

      list.push(newStep, index++, groupIndex)
      return
    }

    for (let i = 0; i < curStep.items.length; ++i) {
      buildLinkedList(
        curStep.items[i],
        path + curStep.href,
        id ? [id, curStep.id].join(".") : curStep.id
      )
    }

    if (path === "") ++groupIndex
  }

  buildLinkedList(
    {
      id: "",
      href: "",
      items: navGroups,
    },
    "",
    ""
  )

  return list
}

export const buildRoutes = (featureFlags: NavFlags): Array<NavGroup> => [
  {
    href: "/personal-info",
    id: "personalInfo",
    items: [
      {
        href: "/contact-information",
        id: "contactInformation",
        items: get(featureFlags.splitContactInfo)
          ? [
              {
                href: "/name",
                id: "name",
              },
              {
                href: "/physical-address",
                id: "physicalAddress",
              },
              {
                href: "/same-address",
                id: "sameAddress",
              },
              {
                href: "/mailing-address",
                id: "mailingAddress",
              },
              {
                href: "/phone-number",
                id: "phoneNumber",
              },
              {
                href: "/sms-notifications",
                id: "smsNotifications",
              },
            ]
          : undefined,
      },
      {
        href: "/citizenship-status",
        id: "citizenshipStatus",
        items: get(featureFlags.splitCitizenshipStatus)
          ? [
              {
                href: "/citizenship",
                id: "citizenship",
              },
              {
                href: "/foreign-citizenship",
                id: "foreignCitizenship",
              },
              {
                href: "/residency",
                id: "residency",
              },
              {
                href: "/visa",
                id: "visa",
              },
              {
                href: "/documents-to-prepare",
                id: "documentsToPrepare",
              },
            ]
          : undefined,
      },
      {
        href: "/tax-information",
        id: "taxInformation",
        items: get(featureFlags.splitTaxInfo)
          ? [
              {
                href: "/tax-number",
                id: "taxNumber",
              },
              {
                href: "/family",
                id: "family",
              },
              {
                href: "/birth-date",
                id: "birthDate",
              },
              {
                href: "/subject-to-tax-withholding",
                id: "subjectToTaxWithholding",
              },
            ]
          : undefined,
      },
      {
        href: "/employment-information",
        id: "employmentInformation",
        items: get(featureFlags.splitEmploymentInfo)
          ? [
              {
                href: "/employment-status",
                id: "employmentStatus",
              },
              {
                href: "/job-details",
                id: "jobDetails",
                hidden: true,
              },
            ]
          : undefined,
      },
      {
        href: "/affiliation-information",
        id: "affiliationInformation",
        items: get(featureFlags.splitAffiliation)
          ? [
              {
                href: "/public-company-affiliation",
                id: "publicCompanyAffiliation",
              },
              {
                href: "/industry-affiliation",
                id: "industryAffiliation",
              },
              {
                href: "/political-affiliation",
                id: "politicalAffiliation",
              },
              {
                href: "/investment-advisor-affiliation",
                id: "investmentAdvisorAffiliation",
              },
              {
                href: "/institutional-assets-affiliation",
                id: "institutionalAssetsAffiliation",
              },
            ]
          : undefined,
      },
    ],
  },
  {
    href: "/account",
    id: "account",
    items: [
      {
        href: "/account-type",
        id: "accountType",
        items: get(featureFlags.splitAccountType)
          ? [
              {
                href: "/select",
                id: "accountTypeSelect",
              },
              {
                href: "/joint",
                id: "jointAccountType",
              },
              {
                href: "/retirement",
                id: "retirementAccountType",
                items: [
                  {
                    href: "/account",
                    id: "retirementAccountSelect",
                  },
                  {
                    href: "/individual",
                    id: "iraIndividualAccountType",
                  },
                  {
                    href: "/beneficiary-inherited",
                    id: "iraBeneficiaryOrInheritedAccountType",
                  },
                  {
                    href: "/decedent-name",
                    id: "decedentName",
                  },
                ],
              },
            ]
          : undefined,
      },
      {
        href: "/add-beneficiaries",
        id: "addBeneficiaries",
        hidden: true,
        items: get(featureFlags.splitBeneficiaryInfo)
          ? [
              {
                href: "/type-of-beneficiary",
                id: "typeOfBeneficiary",
              },
              {
                href: "/beneficiaries",
                id: "beneficiaries",
              },
              {
                href: "/beneficiary-name",
                id: "beneficiaryName",
              },
              {
                href: "/beneficiary-details",
                id: "beneficiaryDetails",
              },
              {
                href: "/beneficiary-address",
                id: "beneficiaryAddress",
              },
              {
                href: "/trust-beneficiary",
                id: "trustBeneficiary",
              },
            ]
          : undefined,
      },
      {
        href: "/additional-information",
        id: "additionalInformation",
        hidden: true,
        items: get(featureFlags.splitAccountType)
          ? [
              {
                href: "/secondary-owner",
                id: "secondaryOwner",
              },
              {
                href: "/estate-percentage-allocation",
                id: "estatePercentageAllocation",
              },
            ]
          : undefined,
      },
      {
        href: "/trading-method",
        id: "tradingMethod",
      },
      {
        href: "/trading-experience",
        id: "tradingExperience",
        items: get(featureFlags.splitTradingExperience)
          ? [
              {
                href: "/instruments-knowledge",
                id: "instrumentsKnowledge",
              },
              {
                href: "/trading-objective",
                id: "tradingObjective",
              },
              {
                href: "/financial-information",
                id: "financialInformation",
              },
            ]
          : undefined,
      },
      {
        href: "/account-access",
        id: "accountAccess",
      },
      {
        href: "/account-activity",
        id: "accountActivity",
        hidden: true,
      },
    ],
  },
  {
    href: "/business",
    id: "business",
    hidden: true,
    items: [
      {
        href: "/contact",
        id: "contactBusiness",
      },
      {
        href: "/relationship",
        id: "relationship",
      },
      {
        href: "/beneficial-owners",
        id: "beneficialOwners",
      },
      {
        href: "/affiliation",
        id: "affiliation",
      },
    ],
  },
  {
    href: "/trust",
    id: "trust",
    hidden: true,
    items: [
      {
        href: "/trust-data",
        id: "trustData",
      },
      {
        href: "/grantor",
        id: "grantor",
      },
      {
        href: "/trustee",
        id: "trustee",
      },
      {
        href: "/beneficiary",
        id: "beneficiary",
      },
      {
        href: "/trust-affiliation",
        id: "trustAffiliation",
      },
    ],
  },
  {
    href: "/security",
    id: "security",
    items: [
      {
        href: "/confirm-identity",
        id: "confirmIdentity",
        hidden: true,
      },
      {
        href: "/two-factor-auth",
        id: "twoFactorAuth",
        items: get(featureFlags.splitTwoFactorAuth)
          ? [
              {
                href: "/setup",
                id: "setup",
              },
              {
                href: "/method-selection",
                id: "methodSelection",
              },
              {
                href: "/sms",
                id: "sms",
              },
            ]
          : undefined,
      },
      {
        href: "/security-question",
        id: "securityQuestion",
      },
      {
        href: "/trusted-contact",
        id: "trustedContact",
      },
    ],
  },
  {
    href: "/documents",
    id: "documents",
    items: [
      {
        href: "/documents-upload",
        id: "documentsUpload",
        items: [
          {
            href: "/confirm",
            id: "confirm",
          },
          {
            href: "/upload",
            id: "upload",
          },
        ],
      },
      {
        href: "/important-documents",
        id: "importantDocuments",
      },
    ],
  },
  {
    href: "/review",
    id: "review",
    items: [
      {
        href: "",
        id: "reviewAndSign",
      },
    ],
  },
  {
    href: "/done",
    id: "done",
    hidden: true,
    items: [
      {
        href: "/auto-approved",
        id: "autoApproved",
        hidden: true,
      },
      {
        href: "/needs-review",
        id: "needsReview",
        hidden: true,
      },
      // Currently, /documents-required will never be shown. Users are only
      // redirected there from /needs-review and there are no validations
      {
        href: "/documents-required",
        id: "documentsRequired",
        hidden: true,
      },
      {
        href: "/joint-continuation",
        id: "jointContinuation",
        hidden: true,
      },
    ],
  },
]

const ACTIVE_STYLE = [
  "before:border-icon-navigation-active",
  "before:-z-10",
  "before:bg-general-surface-01",
  "before:w-3",
  "before:h-3",
  "before:rounded-full",
  "before:absolute",
  "before:border",
  "before:absolute-center",
]
const ENABLED_STYLE =
  "bg-[var(--color-icon-navigation-active)] text-general-inverted-text"

const circleVariants = cva("rounded-full border border-transparent", {
  variants: {
    size: {
      small: "h-2 w-2",
      large: "flex h-6 w-6 items-center justify-center",
    },
    state: {
      enabled: ENABLED_STYLE,
      complete: ENABLED_STYLE,
      review: ENABLED_STYLE,
      disabled:
        "border-icon-navigation-inactive bg-general-body text-navigation-page-label-inactive",
      "in-progress":
        "border-icon-navigation-active bg-general-body text-icon-general-primary",
    },
    active: {
      true: ACTIVE_STYLE,
      false: "z-10",
    },
  },
  compoundVariants: [
    {
      state: "in-progress",
      size: "small",
    },
  ],
})

type CircleVariants = Parameters<typeof circleVariants>[0]
export const circle = (variants: CircleVariants) => cn(circleVariants(variants))

const textVariants = cva("transition-colors", {
  variants: {
    size: {
      small: "font-medium-500 md:font-small-500",
      large: "font-large-500 md:font-medium-600",
    },
    state: {
      enabled: "text-input-fields-input-active",
      disabled: "text-navigation-page-label-inactive",
      active: "text-navigation-page-label-active",
      "in-progress": "text-input-fields-input-active",
      review: "text-input-fields-input-active",
      complete: "text-input-fields-input-active",
    },
  },
})

type TextVariants = Parameters<typeof textVariants>[0]
export const text = (variants: TextVariants) => cn(textVariants(variants))

const lineVariants = cva(`absolute-center-x absolute h-10 w-px origin-top`, {
  variants: {
    size: {
      small: "bottom-1/2 -z-20",
      large: "top-full h-4",
    },
    disabled: {
      false: "bg-[var(--color-icon-navigation-active)]",
      true: "bg-[var(--color-icon-navigation-inactive)]",
    },
  },
})

type LineVariants = Parameters<typeof lineVariants>[0]
export const line = (variants: LineVariants) => cn(lineVariants(variants))
